'use client';

import { getHref, ROUTE } from '@utils/route-utils';
import { ShieldAlert } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import type { FC } from 'react';
import AllergenBadges from './AllergenBadges';
import { useActiveAllergens } from './use-active-allergens';

type RecipeAllergensProps = { organizationId: string; recipeId: string };

/**
 * The recipe's allergens from the active allergy matrix, on the recipe page.
 * Renders nothing until a matrix is active; a recipe the matrix does not
 * list says so instead of implying it is allergen-free.
 */
const RecipeAllergens: FC<RecipeAllergensProps> = ({
  organizationId,
  recipeId
}) => {
  const t = useTranslations('allergy-matrix');
  const { byRecipe, notesByRecipe, customAllergens, matrixId, loaded } =
    useActiveAllergens(organizationId);
  if (!loaded || !matrixId) {
    return null;
  }
  const allergens = byRecipe.get(recipeId);
  const notes = notesByRecipe.get(recipeId) ?? [];
  return (
    <section className="space-y-3 rounded-xl bg-card p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-card-title text-foreground">
          <ShieldAlert className="size-5" aria-hidden />
          {t('recipe-title')}
        </h2>
        <Link
          href={getHref(organizationId, [ROUTE.FOOD, ROUTE.ALLERGY_MATRIX])}
          className="text-caption font-medium text-brand-text hover:opacity-80"
        >
          {t('recipe-open-matrix')}
        </Link>
      </div>
      {allergens ? (
        <AllergenBadges
          allergens={allergens}
          customAllergens={customAllergens}
          emptyLabel={t('recipe-none')}
        />
      ) : (
        <p className="text-caption text-muted-foreground">
          {t('recipe-not-listed')}
        </p>
      )}
      {notes.map(note => (
        <p key={note} className="text-caption text-muted-foreground">
          {note}
        </p>
      ))}
    </section>
  );
};

export default RecipeAllergens;
